import { useState } from 'react';
import { Card } from '@design/Card';
import { Button } from '@design/Button';
import { useEventBus } from '@bus/useEventBus';
import type { WorkedExample as WE } from '@curriculum/types';

/**
 * Worked-example renderer with adaptive fading.
 *
 * Cognitive Load Theory says novices learn faster from front-loaded worked
 * examples than from pure problem-solving. Adaptive fading reveals one step at
 * a time so the student processes each schema element (4±1 working-memory cap)
 * before the next arrives.
 *
 * Evidence: Salden et al. 2010 (CMU Tutoring Lab); Barbieri et al. 2023
 * meta-analysis (g=0.48 across 55 studies, 181 effect sizes).
 */
export function WorkedExample({ chapterId, example }: { chapterId: string; example: WE }) {
  const emit = useEventBus();
  const [revealedSteps, setRevealedSteps] = useState(1);
  const [showConclusion, setShowConclusion] = useState(false);

  const advance = () => {
    if (revealedSteps < example.steps.length) {
      const next = revealedSteps + 1;
      setRevealedSteps(next);
      emit({ kind: 'dialogue.advance', payload: { simId: `worked-example:${chapterId}`, step: next, direction: 'next' } });
    } else if (!showConclusion) {
      setShowConclusion(true);
      emit({ kind: 'dialogue.advance', payload: { simId: `worked-example:${chapterId}`, step: example.steps.length + 1, direction: 'next' } });
    }
  };

  const isDone = revealedSteps === example.steps.length && showConclusion;

  return (
    <Card tone="accent" className="!p-0 overflow-hidden shadow-card-raised !rounded-card-lg">
      <div className="h-0.5 bg-accent-500" />
      <header className="px-5 sm:px-6 py-4 border-b border-ink-200/80 bg-accent-50 flex items-center justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-accent-600 font-semibold">Worked example</div>
          <p className="text-ink-900 mt-1 text-[15px] font-medium leading-relaxed">{example.problem}</p>
        </div>
        <span className="text-xs text-ink-500 font-mono whitespace-nowrap">
          {Math.min(revealedSteps, example.steps.length)} / {example.steps.length}
        </span>
      </header>

      <ol className="divide-y divide-ink-200">
        {example.steps.slice(0, revealedSteps).map((step, i) => (
          <li key={i} className={`px-5 sm:px-6 py-5 bg-cream ${i % 2 === 0 ? 'animate-post-debit' : 'animate-post-credit'} motion-reduce:animate-none`}>
            <div className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-100 font-mono text-xs font-semibold text-accent-700">{i + 1}</span>
              <div className="flex-1">
                <div className="text-base font-medium text-ink-900">{step.statement}</div>
                {step.calculation && (
                  <pre className="mt-2.5 overflow-x-auto px-3.5 py-2.5 rounded-lg border border-ink-200/70 bg-ink-50 text-sm font-mono text-ink-700 whitespace-pre-wrap">{step.calculation}</pre>
                )}
                <div className="text-sm text-ink-600 mt-2 leading-relaxed">{step.explanation}</div>
              </div>
            </div>
          </li>
        ))}
      </ol>

      {showConclusion && (
        <div className="px-5 py-4 bg-econ-50 border-t border-econ-200">
          <div className="text-xs uppercase tracking-wide text-econ-600 font-semibold">Conclusion</div>
          <div className="text-ink-900 mt-1">{example.conclusion}</div>
        </div>
      )}

      <footer className="px-5 sm:px-6 py-3.5 bg-ink-50/80 border-t border-ink-200/80 flex justify-between items-center gap-3">
        <span className="text-xs text-ink-500">
          {isDone ? 'Solved — try it yourself in the practice block below.' : 'Reveal one step at a time. Read each before the next.'}
        </span>
        {!isDone && <Button onClick={advance}>Next step →</Button>}
      </footer>
    </Card>
  );
}
